import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useTenant, isUuid } from './TenantContext';
import authService from '../services/authService';
import facilityService from '../services/facilityService';
import attendanceService from '../services/attendanceService';
import { useSessionActivity } from '../hooks/useSessionActivity';
import { getCurrentBrowserTime } from '../utils/dateUtils';
import {
  SESSION_TIMEOUT_MS,
  PROACTIVE_REFRESH_THRESHOLD_MS,
  SESSION_CHECK_INTERVAL_MS
} from '../constants/session';



interface User {
  id: string;
  username: string;
  email?: string;
  name?: string;
  role: 'NURSE' | 'ADMIN' | 'DOCTOR' | 'SOCIAL_WORKER' | 'FACILITY_ADMIN' | 'CLIENT_ADMIN' | 'SUPER_ADMIN' | 'CAREGIVER' | 'LEAD_CAREGIVER';
  tenantId?: string;
  facilityId?: string;
  clientId?: string;
  token?: string;
  facility?: { id: string; name: string }
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const { currentTenantId, setCurrentTenantId, setTenant } = useTenant();

  const handleActiveAfterSilence = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      console.log('[Session] User active after silence. Proactively refreshing token immediately.');
      try {
        const newToken = await authService.refreshToken();
        if (newToken) {
          const refreshedFacilityId = authService.getFacilityIdFromToken(newToken);
          if (refreshedFacilityId && isUuid(refreshedFacilityId)) {
            setCurrentTenantId(refreshedFacilityId);
            localStorage.setItem('currentTenantId', refreshedFacilityId);
          }
          setUser(prevUser => {
            if (!prevUser) return null;
            const updatedUser = { 
              ...prevUser, 
              token: newToken,
              ...(refreshedFacilityId && isUuid(refreshedFacilityId) ? { tenantId: refreshedFacilityId, facilityId: refreshedFacilityId } : {})
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return updatedUser;
          });
          localStorage.setItem('lastTokenRefreshTime', String(Date.now()));
        }
      } catch (err) {
        console.error('Proactive refresh on active after silence failed:', err);
      }
    }
  }, []);

  const lastActiveTimeRef = useSessionActivity({
    onActiveAfterSilence: handleActiveAfterSilence,
    silenceThresholdMs: SESSION_TIMEOUT_MS
  });

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      // Use real Keycloak authentication
      const { token, user: userData } = await authService.login(username, password);

      let fullFacilities: any[] = [];
      try {
        const userRole = userData.role?.toUpperCase();
        if (userRole === "SUPER_ADMIN" || userRole === "ADMIN") {
          const res = await facilityService.getAllFacilities(0, 100);
          fullFacilities = (res as any)?.content ?? (res as any)?.items ?? (Array.isArray(res) ? res : []);
        } else {
          fullFacilities = await facilityService.getFacilitiesByUser(userData.id);
        }
      } catch(e) { 
        console.error("Failed to fetch full facilities during login", e); 
      }
      
      const primaryFacilityFromApi = fullFacilities.find((f: any) => f.isPrimary === true);

      const primaryFacility = primaryFacilityFromApi || (Array.isArray(userData.facility)
        ? (userData.facility.find((f: any) => f.isPrimary === true) || userData.facility[0])
        : userData.facility);
        
      const facilityId = primaryFacility?.id;

      const authenticatedUser: User = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        token,
        tenantId: facilityId || userData.tenantId || currentTenantId || undefined,
        facility: userData.facility || undefined
      };

      try {
        if (facilityId) {
          const loginTime = getCurrentBrowserTime();
          // Await auto check-in on login so session is ready in localStorage
          try {
            const session = await attendanceService.autoCheckIn(facilityId, loginTime);
            localStorage.setItem('activeAttendanceSession', JSON.stringify(session));
          } catch (err: any) {
            console.error("Auto check-in failed", err);
          }
        }
      } catch (e: any) {
        console.error("Auto check-in error", e);
      }

      setUser(authenticatedUser);
      localStorage.setItem('user', JSON.stringify(authenticatedUser));
      localStorage.setItem('lastActiveTime', String(Date.now()));
      localStorage.setItem('lastTokenRefreshTime', String(Date.now()));

      // Update TenantContext immediately so facility-scoped queries work right after login
      if (facilityId) {
        setCurrentTenantId(facilityId);
        const facilityName = primaryFacility?.name || facilityId;
        setTenant({
          id: facilityId,
          name: facilityName || facilityId,
        });
        localStorage.setItem('currentTenantId', facilityId);
      }

      return true;
    } catch (error: any) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setTenant(null);
    setCurrentTenantId(null);
    localStorage.removeItem('user');
    localStorage.removeItem('currentTenantId');
    localStorage.removeItem('lastTokenRefreshTime');
  };

  // Check if user is authenticated on initial load
  const checkAuthStatus = () => {
    if (!user) {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('authToken');

      if (storedUser && storedToken) {
        try {
          // Rehydrate facility UUID from JWT token claims only if not already a valid UUID
          const tokenFacilityId = authService.getFacilityIdFromToken(storedToken);
          const hasValidFacility = isUuid(currentTenantId) || isUuid(localStorage.getItem('currentTenantId'));
          if (!hasValidFacility && tokenFacilityId && isUuid(tokenFacilityId)) {
            setCurrentTenantId(tokenFacilityId);
            localStorage.setItem('currentTenantId', tokenFacilityId);
          }

          // Check if token is still valid
          if (!authService.isTokenExpired(storedToken)) {
            const parsedUser = JSON.parse(storedUser) as User;
            if (!isUuid(parsedUser.tenantId) && tokenFacilityId && isUuid(tokenFacilityId)) {
              parsedUser.tenantId = tokenFacilityId;
              parsedUser.facilityId = tokenFacilityId;
            }

            // Allow SUPER_ADMIN and CLIENT_ADMIN to login without tenant check
            // For other users, verify tenant match if both tenant IDs exist
            if (
              parsedUser.role === 'SUPER_ADMIN' ||
              parsedUser.role === 'CLIENT_ADMIN' ||
              !parsedUser.tenantId ||
              !currentTenantId ||
              parsedUser.tenantId === currentTenantId ||
              (tokenFacilityId && isUuid(tokenFacilityId))
            ) {
              setUser(parsedUser);
            } else {
              // If tenant mismatch, clear the stored user
              localStorage.removeItem('user');
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
            }
          } else {
            // Token expired, try to refresh
            authService.refreshToken().then((newToken) => {
              if (newToken) {
                const refreshedFacilityId = authService.getFacilityIdFromToken(newToken);
                if (refreshedFacilityId && isUuid(refreshedFacilityId)) {
                  setCurrentTenantId(refreshedFacilityId);
                  localStorage.setItem('currentTenantId', refreshedFacilityId);
                }
                const parsedUser = JSON.parse(storedUser) as User;
                parsedUser.token = newToken;
                if (refreshedFacilityId && isUuid(refreshedFacilityId)) {
                  parsedUser.tenantId = refreshedFacilityId;
                  parsedUser.facilityId = refreshedFacilityId;
                }
                setUser(parsedUser);
                localStorage.setItem('user', JSON.stringify(parsedUser));
                localStorage.setItem('lastTokenRefreshTime', String(Date.now()));
              } else {
                // Refresh failed, clear everything
                localStorage.removeItem('user');
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
              }
            });
          }
        } catch (e) {
          console.error('Error parsing stored user', e);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
        }
      }
    }
  };

  // Check auth status when tenant changes
  useEffect(() => {
    checkAuthStatus();
  }, [currentTenantId]);

  // Auto-refresh token proactively before expiry, or log out if inactive
  useEffect(() => {
    const isAuthenticatedState = !!user || !!localStorage.getItem('authToken');
    if (!isAuthenticatedState) return;

    const interval = setInterval(
      async () => {
        const refTime = lastActiveTimeRef.current;
        const storedTime = parseInt(localStorage.getItem('lastActiveTime') || '0', 10);
        const lastActive = Math.max(refTime, storedTime);

        const now = Date.now();
        const timeSinceLastActive = now - lastActive;
        const token = localStorage.getItem('authToken');
        const isExpired = token ? authService.isTokenExpired(token) : true;

        if (timeSinceLastActive >= SESSION_TIMEOUT_MS && isExpired) {
          // User inactive for 15 minutes, log them out
          // If inactive for 15m AND either token is expired OR we just want to force logout on inactivity
          // The user requested: "if any one is not working on that screen ... log out automatically"
          console.warn('[Session] Inactive timeout reached — logging out.');
          authService.logout().then(() => {
            setUser(null);
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('lastActiveTime');
            localStorage.removeItem('lastTokenRefreshTime');
            localStorage.removeItem('activeAttendanceSession');
            window.location.href = '/login?reason=timeout';
          });
        } else {
          // User is active (last interaction was < 15m ago)!
          // Extend session by proactively refreshing token if it's "old"
          const token = localStorage.getItem('authToken');
          if (token) {
            const isExpired = authService.isTokenExpired(token);
            const lastRefresh = parseInt(localStorage.getItem('lastTokenRefreshTime') || '0', 10);
            const timeSinceLastRefresh = now - lastRefresh;

            // If the token is expired, OR if the user is active and it's been more than 5 minutes 
            // since the last refresh, we refresh to "extend" the backend session.
            if (isExpired || timeSinceLastRefresh > PROACTIVE_REFRESH_THRESHOLD_MS) {
              console.debug('[Session] User active — proactively refreshing token to extend session.');
              try {
                const newToken = await authService.refreshToken();
                if (newToken) {
                  // Update user state if we have a user object
                  if (user) {
                    setUser(prevUser => {
                      if (!prevUser) return null;
                      const updatedUser = { ...prevUser, token: newToken };
                      localStorage.setItem('user', JSON.stringify(updatedUser));
                      return updatedUser;
                    });
                  }
                  localStorage.setItem('lastTokenRefreshTime', String(Date.now()));
                }
              } catch (err) {
                console.error('Proactive token refresh failed', err);
              }
            }
          }
        }
      },
      SESSION_CHECK_INTERVAL_MS
    );

    return () => clearInterval(interval);
  }, [user]);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};